import React, { useMemo, useState } from 'react';
import { Button, useToast, type DateRange } from '@crisellevaldez/speculo-ui';
import { HiExclamationCircle } from 'react-icons/hi';
import { EmptyStateWithIcon } from '../../../common/EmptyStateWithIcon';
import { useUpdateContactConsent } from '../../../../hooks/contacts/useContactConsent';
import { useContactConsentGQL } from '../../../../hooks/graphql/contacts/useContactConsentGQL';
import { useOrganizationUsers } from '../../../../hooks/graphql/users/useOrganizationUsers';
import { useUser } from '../../../../hooks/useUser';
import { useOrganizationStore, useFlywheelEnabled } from '../../../../stores/useOrganizationStore';
import type { ContactConsentUpdate } from '../../../../api/consent';
import type { Contact } from '../../../../types/contact.types';
import { CommunicationConsentSection } from './CommunicationConsentSection';
import { mapContactConsent } from './mapContactConsent';
import { mapConsentHistory, type ResolvedUser } from './mapConsentHistory';
import { canToggleConsent, resolveConsentViewState } from './consentViewState';
import { handleDateRangeChange as convertDateRange } from '../../../../utils/dateRangeTimezone';
import type { ConsentChannel, ConsentHistoryFilter } from './consent.types';

/** The ledger stores the voice channel as "voice"; the UI calls it "phone". */
const LEDGER_CHANNEL: Record<Exclude<ConsentHistoryFilter, 'all'>, string> = {
  phone: 'voice',
  sms: 'sms',
  email: 'email',
};

/** UI channel names differ from the API's: the voice channel is "phone" here. */
const CONSENT_FIELD: Record<ConsentChannel, keyof ContactConsentUpdate> = {
  phone: 'voice',
  sms: 'sms',
  email: 'email',
};

const HISTORY_PAGE_SIZE = 20;

/** Recorded on the ledger row so an exported trail names the surface used. */
const CHANGE_REASON = 'Updated from the contact Consent tab';

const CHANNEL_NOUN: Record<ConsentChannel, string> = {
  phone: 'Calls',
  sms: 'SMS',
  email: 'Email',
};

interface ConsentTabProps {
  contact?: Contact | null;
  /** Refreshes the parent contact read, which carries consent for other surfaces. */
  onContactUpdate?: () => void | Promise<void>;
}

export const ConsentTab: React.FC<ConsentTabProps> = ({ contact, onContactUpdate }) => {
  const { showToast } = useToast();
  const { isSuperAdmin } = useUser();
  const { organization } = useOrganizationStore();
  const flywheelEnabled = useFlywheelEnabled();
  const [updatingChannel, setUpdatingChannel] = useState<ConsentChannel | null>(null);
  // Trail filtering is server-side against one exact channel, so an org without
  // SMS opens on Calls rather than "all" — "all" would include SMS rows.
  const [historyFilter, setHistoryFilter] = useState<ConsentHistoryFilter>(
    flywheelEnabled ? 'all' : 'phone'
  );
  const [historyDateRange, setHistoryDateRange] = useState<DateRange>({ from: null, to: null });
  // Zero-based, matching OffsetPagination and every other caller of it. The
  // component renders `currentPage + 1`, so a one-based page here opened the
  // trail labelled "2", left the final page unreachable, and sent offset -20
  // when the first page was clicked.
  const [historyPage, setHistoryPage] = useState(0);

  const contactId = contact?.id ?? '';

  // Cards and timeline share one response, so they cannot show different states.
  const {
    consent,
    history: historyEvents,
    historyTotal,
    loading,
    error,
    refetch,
  } = useContactConsentGQL({
    contactId,
    enabled: !!contactId,
    limit: HISTORY_PAGE_SIZE,
    offset: historyPage * HISTORY_PAGE_SIZE,
    channel: historyFilter === 'all' ? null : LEDGER_CHANNEL[historyFilter],
    dateFrom: historyDateRange.from?.toISOString() ?? null,
    dateTo: historyDateRange.to?.toISOString() ?? null,
  });

  // Ledger `actor` holds a Spanner user id, which the org roster is keyed by.
  const { users } = useOrganizationUsers();
  const actors = useMemo<Record<string, ResolvedUser>>(
    () => Object.fromEntries(users.map((u) => [u.id, { name: u.label, role: u.role || null }])),
    [users]
  );

  const history = useMemo(() => mapConsentHistory(historyEvents, actors), [historyEvents, actors]);

  // SMS is a flywheel-only channel: an org that cannot send it has no SMS
  // consent to manage. The ledger itself is untouched — this hides the channel
  // from the UI only, and server-side enforcement is unaffected.
  const channels = useMemo(() => {
    const all = mapContactConsent(consent, actors);
    return flywheelEnabled ? all : all.filter((c) => c.channel !== 'sms');
  }, [consent, actors, flywheelEnabled]);

  // A filter change can leave the offset past the end of the new result set,
  // which would render an empty page — send the user back to the first one.
  const resetToFirstPage = () => setHistoryPage(0);

  const updateConsent = useUpdateContactConsent();

  const handleToggle = async (channel: ConsentChannel, optedIn: boolean) => {
    if (!contactId || !isSuperAdmin) return;
    // The switch is already disabled for a suppressed channel; re-checking here
    // keeps a stale render from writing an opt-in the resolver would discard
    // while this surface reported consent granted.
    if (!canToggleConsent(channels, channel)) {
      showToast({
        message: `${CHANNEL_NOUN[channel]} consent is set by the contact's status field and cannot be changed here`,
        variant: 'error',
      });
      return;
    }
    setUpdatingChannel(channel);
    try {
      await updateConsent.mutateAsync({
        contactId,
        update: { [CONSENT_FIELD[channel]]: optedIn, reason: CHANGE_REASON },
      });
      await refetch();
      await onContactUpdate?.();
      showToast({
        message: `${CHANNEL_NOUN[channel]} consent ${optedIn ? 'granted' : 'revoked'}`,
        variant: 'success',
      });
    } catch (err) {
      showToast({
        message: err instanceof Error ? err.message : 'Could not update consent',
        variant: 'error',
      });
    } finally {
      setUpdatingChannel(null);
    }
  };

  const viewState = resolveConsentViewState({ error, loading, channelCount: channels.length });

  if (viewState === 'error') {
    return (
      <div className="flex flex-grow flex-col items-center justify-center gap-3 p-6 text-center">
        <HiExclamationCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-foreground">Couldn&apos;t load consent</p>
          <p className="max-w-sm text-xs text-muted-foreground">
            This contact&apos;s consent record is unavailable right now. It has not been changed —
            don&apos;t treat the channels as unset.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (viewState !== 'ready') {
    return (
      <EmptyStateWithIcon
        message={
          viewState === 'loading'
            ? 'Loading consent record…'
            : 'No consent record for this contact yet'
        }
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-grow flex-col">
      <CommunicationConsentSection
        channels={channels}
        history={history}
        historyTotal={historyTotal}
        historyPage={historyPage}
        historyPageSize={HISTORY_PAGE_SIZE}
        onHistoryPageChange={setHistoryPage}
        historyFilter={historyFilter}
        onHistoryFilterChange={(next) => {
          resetToFirstPage();
          setHistoryFilter(next);
        }}
        historyDateRange={historyDateRange}
        onHistoryDateRangeChange={(next) => {
          resetToFirstPage();
          // The picker returns midnight for both ends, so the raw range asks for
          // `created_at <= <start of the last day>` and drops that whole day —
          // picking today alone matched nothing. Same conversion the Calls and
          // Activity tabs use, which widens `to` to end-of-day in the org zone.
          const { dateRange } = convertDateRange(next, organization?.timezone);
          setHistoryDateRange(dateRange);
        }}
        historyLoading={loading}
        // Manual consent changes are a super-admin action; everyone else sees
        // the cards read-only.
        onChannelToggle={isSuperAdmin ? handleToggle : undefined}
        updatingChannel={updatingChannel}
        timeZone={organization?.timezone}
      />
    </div>
  );
};
